import { DOCUMENT } from "@angular/common";
import { Inject, Injectable, OnDestroy } from "@angular/core";
import { SwUpdate, VersionEvent, VersionReadyEvent } from "@angular/service-worker";
import { IdleService } from "@core/services/idle.service";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, Observable, Subscription } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class VersionCheckService implements OnDestroy {
  private _subscription: Subscription;
  private _pageLoadTime: number;
  private _updateAvailableSubject = new BehaviorSubject<boolean>(false);

  updateAvailable$: Observable<boolean> = this._updateAvailableSubject.asObservable();

  constructor(
    public readonly swUpdate: SwUpdate,
    public readonly translateService: TranslateService,
    public readonly idleService: IdleService,
    @Inject(DOCUMENT) public readonly document: any
  ) {
    this._pageLoadTime = Date.now();
  }

  checkForUpdates() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    if (!!this._subscription) {
      return;
    }

    this._subscription = this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
      if (event.type === "VERSION_READY") {
        const versionReadyEvent = event as VersionReadyEvent;
        this._handleVersionReady(versionReadyEvent);
      }
    });

    void this.swUpdate.checkForUpdate();
  }

  private _handleVersionReady(versionReadyEvent: VersionReadyEvent): void {
    const timeSincePageLoad = Date.now() - this._pageLoadTime;
    const isRecentPageLoad = timeSincePageLoad < 90000; // 90 seconds

    if (isRecentPageLoad) {
      // Fresh page - just opened after server update
      // Silently activate the new service worker, no page reload needed
      void this.swUpdate.activateUpdate();
    } else {
      // Page has been open for a while, real update detected
      const isIdle = this.idleService.isUserIdle();

      if (isIdle) {
        // User is idle - auto reload
        this.activateAndReload();
      } else {
        // User is active - show badge
        this._updateAvailableSubject.next(true);
      }
    }
  }

  activateAndReload(): void {
    void this.swUpdate.activateUpdate().then(() => {
      this.document.location.reload();
    });
  }

  dismissUpdate(): void {
    this._updateAvailableSubject.next(false);
  }

  isUpdateAvailable(): boolean {
    return this._updateAvailableSubject.value;
  }

  ngOnDestroy() {
    if (this._subscription) {
      this._subscription.unsubscribe();
    }
  }
}
